/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {
  useState, useRef
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { log, LOG_LEVELS } from '@common/config/app_logger';
import {
  validateApplePayMerchantApi, editPaymentSourceApi, createCreditCardProcessingPurchaseApi, getCreditCardProcessingDataApi
} from '@common/api/api';
import PollUserCreditStatus from './poll_user_credit_status';
import {
  mpTrackPurchaseStartedEvent, mpTrackPurchaseEvent, mpTrackPurchaseFailedEvent, getFollowupParam, renderButtonTitle, isAffiliatePromoPopup, selectCloseButtonActionIfNeed, buildEventData
} from '../../app/components/purchases/purchases_utils';
import ProcessingStatus from '../../app/components/purchases/processing_status/processing_status';
import { hidePaymentDetails } from '../../app/actions/funds';
import { getUser } from '../../app/actions/user';
import { setMessageToAppData } from '../../app/actions/message_to_app';
import { messageToAppKeys } from '../message_to_app_handler';
import {
  fromMobileApp, browserContext
} from '../config/utils';
import classes from './classes.module.scss';

const ProcessApplePay = ({
  children = null,
  disabled = false,
  id,
  purchaseAmount,
  context,
  purchaseURL = '',
  kind = 'ap',
  chatType = '',
  bonus = 0,
  onProcessing = null,
  isContinue = false,
  type = null,
  currentPaymentOption = null,
  onPurchaseComplete = null,
  cancelChatOnPP = null,
  hideAffiliatePromoPopupAction = null,
  purchaseFees = null,
  clickSource = '',
  notShowPurchaseSuccessPoppup = false,
  advisorId = null,
  topupFlow,
  renderStatusPopup = true,
  onClickHandler = null,
  onPurchaseFailed = null
}) => {
  const location = useLocation();
  const { t } = useTranslation();
  const [processing, setProcessing] = useState(false);
  const appleSesionRef = useRef(null);
  const { merchantId, ...applePaySession } = context || {};
  const [userCreditId, setUserCreditId] = useState(null);
  const [paymentResponse, setPaymentResponse] = useState(null);

  if (!merchantId || !id || !purchaseAmount) { return null; }

  if (!applePaySession.amount) {
    applePaySession.amount = purchaseAmount;
  }

  const handlePurchase = (purchaseData) => {
    const {
      conversionEventId, extraEventParams, okMessage, cashbackAmount, pathname
    } = purchaseData;

    if (fromMobileApp) {
      const purchaseProps = buildEventData({
        type, purchaseAmount, bonus, currentPaymentOption, chatType, extraEventParams, clickSource
      });
      window.store.dispatch(setMessageToAppData({
        sentToAppMessage: {
          [messageToAppKeys.action]: messageToAppKeys.onPayCompeted,
          [messageToAppKeys.params]: {
            [messageToAppKeys.okMessage]: okMessage,
            [messageToAppKeys.cashback]: cashbackAmount ? parseFloat(cashbackAmount).toFixed(2) : null,
            analytic_events: {
              Purchase: {
                ...purchaseProps
              }
            }
          }
        }
      }));
      // need for send onSubmit message to app for ADVISOR BONUS
      if (onPurchaseComplete && notShowPurchaseSuccessPoppup) {
        onPurchaseComplete();
      }
    } else {
      mpTrackPurchaseEvent({
        type, purchaseAmount, bonus, purchaseFees, conversionEventId, dispatch: window.store.dispatch,
        extraEventParams, currentPaymentOption, chatType, pathname, location, advisorId
      });
      window.store.dispatch(getUser());
    }
  };

  const handleError = (response, error = null) => {
    const { errorMessage } = response;
    const props = {
      ...response,
      errorMessage: errorMessage ?? error
    };
    if (fromMobileApp) {
      return;
    }
    mpTrackPurchaseFailedEvent({
      ...props, type, purchaseAmount, bonus, purchaseFees, dispatch: window.store.dispatch, chatType, currentPaymentOption, location, advisorId
    });
    if (onPurchaseFailed) {
      onPurchaseFailed(errorMessage);
    }
  };

  const trackPurchaseStarted = (dataResponse) => {
    if (fromMobileApp) {
      const purchaseStartedProps = buildEventData({
        type, purchaseAmount, bonus, currentPaymentOption, chatType, purchaseFees, clickSource
      });
      window.store.dispatch(setMessageToAppData({
        sentToAppMessage:{
          [messageToAppKeys.action]: messageToAppKeys.analytics,
          [messageToAppKeys.params]: {
            analytic_events: {
              'purchase started': {
                ...purchaseStartedProps
              }
            }
          }
        }
      }));
      return;
    }
    mpTrackPurchaseStartedEvent({
      type, purchaseAmount: parseFloat(purchaseAmount), bonus, purchaseFees, dispatch: window.store.dispatch, ...dataResponse, currentPaymentOption, chatType, location, advisorId
    });
  };

  const updateProcesing = (p, processingResponse = null) => {
    setProcessing(p);
    if (onProcessing) {
      onProcessing(p, processingResponse);
    }
  };

  const onAppleError = (err) => {
    updateProcesing(false);
    handleError(null, err);
  };

  const startUserCreditPolling = () => {
    const params = {
      use_reference_transaction: true,
      with3ds: true,
      ...getFollowupParam({ isContinue, type, topupFlow }),
      ...browserContext
    };

    getCreditCardProcessingDataApi({ url: purchaseURL, params }).then((res) => {
      const createPurchaseParams = {
        creditRequested: purchaseAmount,
        ...res.requiredParams,
        usePaymentSource_id: id,
        ...browserContext
      };
      trackPurchaseStarted(res);
      log(LOG_LEVELS.INFO, 'Apple Pay', `Creating credit card processing with Data res: ${ res }, paymentSource id: ${ id }, purchase Amount:${ purchaseAmount }`);
      createCreditCardProcessingPurchaseApi(createPurchaseParams).then((outcome) => {
        if (kind === 'ap' && !appleSesionRef.current) {
          return;
        }
        setUserCreditId(outcome.id);
      }).catch((err) => {
        onAppleError(err);
      });
    });
  };

  const onClick = (e) => {
    e.preventDefault();
    if (disabled || processing) { return; }

    if (onClickHandler) {
      onClickHandler();
    }

    updateProcesing(true);
    if (kind === 'am') {
      startUserCreditPolling();
      return;
    }

    const aps = { ...applePaySession };
    aps.total.amount = purchaseAmount;

    try {
      appleSesionRef.current = new window.ApplePaySession(6, aps);
    } catch (error) {
      onAppleError(error);
      return;
    }

    appleSesionRef.current.oncancel = () => {
      updateProcesing(false);
      mpTrackPurchaseFailedEvent({
        reason: 'user canceled', type, purchaseAmount, bonus, purchaseFees, dispatch: window.store.dispatch, chatType, currentPaymentOption, location, clickSource, advisorId
      });
    };

    appleSesionRef.current.onvalidatemerchant = ({ validationURL }) => {
      validateApplePayMerchantApi(id, validationURL).then((merchantSession) => {
        if (appleSesionRef.current) {
          appleSesionRef.current.completeMerchantValidation(merchantSession);
        }
      }).catch((error) => {
        try {
          if (appleSesionRef.current) { appleSesionRef.current.abort(); }
        } catch (er) {
          onAppleError(er);
        }
        onAppleError(error);
      });
    };

    appleSesionRef.current.onpaymentauthorized = (event) => {
      const { payment } = event || {};
      if (!payment || !payment.token) {
        if (appleSesionRef.current) {
          appleSesionRef.current.completePayment(window.ApplePaySession.STATUS_FAILURE);
        }
        onAppleError('Apple processing failure');
      }

      appleSesionRef.current.completePayment(window.ApplePaySession.STATUS_SUCCESS);

      const { token, billingContact, shippingContact } = payment;

      editPaymentSourceApi({ id, params: { context: { token, billingContact, shippingContact } }, type: 'updatePaymentSourceApi' })
        .then(() => {
          log(LOG_LEVELS.INFO, 'Apple Pay', 'on edit payment source API res');
          startUserCreditPolling();
        }).catch(() => {
          log(LOG_LEVELS.INFO, 'Apple pay', 'On edit payment sources error');
          if (appleSesionRef.current) {
            appleSesionRef.current.completePayment(window.ApplePaySession.STATUS_FAILURE);
            onAppleError('Error on edit source');
          }
        });
    };
    appleSesionRef.current.begin();
  };

  const onUserCreditStatusChange = (isSuccess, response) => {
    updateProcesing(false, response);
    setPaymentResponse(response);
    if (isSuccess) {
      handlePurchase(response);
    } else {
      handleError(response);
    }
  };

  const dissmiss = () => {
    window.store.dispatch(hidePaymentDetails());
  };

  const onConfirmButtonClick = () => {
    dissmiss();
    setPaymentResponse(null);
    if (onPurchaseComplete) {
      onPurchaseComplete();
    }
  };

  const onCloseButtonClick = () => {
    dissmiss();
    setPaymentResponse(null);
    selectCloseButtonActionIfNeed({ type, cancelChatOnPP, hideAffiliatePromoPopup: hideAffiliatePromoPopupAction });
  };

  const paymentPopup = () => {
    if (fromMobileApp) return null;
    if (processing || !!paymentResponse) {
      return (
        <ProcessingStatus
          purchaseResponce={ paymentResponse || {} }
          visible={ renderStatusPopup }
          purchaseAmount={ purchaseAmount }
          isAffiliatePromoPopup={ isAffiliatePromoPopup({ type }) }
          isCreditCard
          chatType={ chatType }
          t={ t }
          onConfirm={ onConfirmButtonClick }
          onClose={ onCloseButtonClick }
          onRetry={ onCloseButtonClick }
          buttonTitle={ renderButtonTitle({ isContinue, type, t }) }
          bonus={ bonus }
          notShowPurchaseSuccessPoppup={ notShowPurchaseSuccessPoppup }
        />
      );
    }
    return null;
  };

  const processingRender = () => {
    if (processing) {
      return (
        <div>
          {
            userCreditId ? (
              <PollUserCreditStatus id={ userCreditId } onChange={ onUserCreditStatusChange } />
            ) : null
          }
          <div style={ { height:'44px', textAlign: 'center', color: 'var(--app-text-primary)' } }>
            {t('purchase.processing_payment')}
          </div>
        </div>
      );
    }
    return (
      <a
        href="#"
        onClick={ onClick }
        style={ {
          height: '100%', width: '100%', cursor: 'pointer'
        } }
      >
        { children }
      </a>
    );
  };

  return (
    <div
      className={ classes.processAppleButtonContainer }
      data-testid="process-apple-pay-a-container"
    >
      {paymentPopup()}
      {processingRender()}
    </div>
  );
};

export default ProcessApplePay;
